import { z } from 'zod';
import { currencyCodeSchema } from './currency.js';
import { DEFAULT_BASE_CURRENCY, DEFAULT_TIMEZONE } from './family.js';
import { permissionKeySchema } from './permissions.js';
import { memberRoleSchema, memberStatusSchema } from './roles.js';
import { timezoneSchema } from './timezone.js';
/** Header a mobile client sends so the refresh token comes back in the body, not a cookie. */
export const CLIENT_TYPE_HEADER = 'X-Client-Type';
export const MOBILE_CLIENT_TYPE = 'mobile';
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .pipe(z.email({ message: 'Enter a valid email address' }));
/** NIST SP 800-63B: length only, no composition rules. */
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters`);
export const displayNameSchema = z.string().trim().min(1).max(80);
export const familyNameSchema = z.string().trim().min(1).max(100);
export const registerRequestSchema = z.object({
    displayName: displayNameSchema,
    email: emailSchema,
    password: passwordSchema,
    familyName: familyNameSchema,
    baseCurrency: currencyCodeSchema.default(DEFAULT_BASE_CURRENCY),
    timezone: timezoneSchema.default(DEFAULT_TIMEZONE),
});
export const loginRequestSchema = z.object({
    email: emailSchema,
    // Not validated against the policy: a login only needs to match what was stored.
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
/** Body for refresh/logout from a mobile client. Web clients send the httpOnly cookie instead. */
export const refreshRequestSchema = z
    .object({
    refreshToken: z.string().min(20).max(200).optional(),
})
    // Web clients may send no body at all.
    .default({});
/** Change password while signed in. The new password follows the same policy as sign-up. */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: passwordSchema,
});
/** The change-password form: the request plus typing the new password a second time. */
export const changePasswordFormSchema = changePasswordRequestSchema
    .extend({ confirmPassword: z.string() })
    .refine((v) => v.confirmPassword === v.newPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
export const authResponseSchema = z.object({
    accessToken: z.string(),
    /** Seconds until the access token expires. */
    expiresIn: z.number().int(),
    /** Only present for mobile clients (X-Client-Type: mobile). */
    refreshToken: z.string().optional(),
});
export const deleteAccountRequestSchema = z.object({
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    /**
     * The owner must type the family's name: deleting the owner's login deletes the whole family,
     * including every money record. Ignored for other members.
     */
    familyName: z.string().max(200).optional(),
});
export const familySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    baseCurrency: currencyCodeSchema,
    timezone: z.string(),
    createdAt: z.string(),
    /**
     * True once the family has a transaction, an exchange rate or an account in another currency:
     * the base currency can no longer change.
     */
    currencyLocked: z.boolean(),
    /** Currencies the family uses, base currency first (Phase 3: USD and LBP). */
    currencies: z.array(currencyCodeSchema).min(1),
});
export const meResponseSchema = z.object({
    user: z.object({ id: z.string().uuid(), email: z.string() }),
    member: z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        role: memberRoleSchema,
        status: memberStatusSchema,
    }),
    family: familySchema,
    permissions: z.array(permissionKeySchema),
});
//# sourceMappingURL=auth.js.map